/** `data.type` the backend puts on the partner deck-activity nudge. */
export const PARTNER_DECK_ACTIVITY_NOTIFICATION_TYPE = "partnerDeckActivity";

/** `data.type` the backend puts on the couple all-cards-unlock push. */
export const PARTNER_ALL_CARDS_UNLOCKED_NOTIFICATION_TYPE =
  "partnerAllCardsUnlocked";

/** `data.type` the backend puts on the delayed character-match reveal push. */
export const CHARACTER_MATCH_NOTIFICATION_TYPE = "characterMatch";

/** Which delivery channel produced a deep link: email CTAs carry `src=email`. */
export type NotificationChannel = "email" | "push";

/**
 * A tap on "It's a match with {Name}!" / "{Name} just played the deck" —
 * deep-links into that character partnership's deck, where the revealed
 * stories are presented.
 */
export interface CharacterMatchPress {
  partnershipId: string;
  characterId?: string;
}

/** A tap on "{Name} just had a go on the deck" — deep-links into that partnership's deck. */
export interface PartnerDeckActivityPress {
  partnershipId: string;
  /** The partner who swiped (the sender of the nudge). */
  partnerId?: string;
}

/** A tap on "{Name} unlocked all cards for you two" — deep-links into that partnership's deck. */
export interface PartnerAllCardsUnlockedPress {
  partnershipId: string;
  /** The partner who purchased the unlock (the sender of the push). */
  partnerId?: string;
}

export type RemoteNotificationPress =
  | {
      type: typeof PARTNER_DECK_ACTIVITY_NOTIFICATION_TYPE;
      channel: NotificationChannel;
      press: PartnerDeckActivityPress;
    }
  | {
      type: typeof PARTNER_ALL_CARDS_UNLOCKED_NOTIFICATION_TYPE;
      channel: NotificationChannel;
      press: PartnerAllCardsUnlockedPress;
    }
  | {
      type: typeof CHARACTER_MATCH_NOTIFICATION_TYPE;
      channel: NotificationChannel;
      press: CharacterMatchPress;
    };

/**
 * Any landing on a notification deep link, including types the app has no
 * in-app destination for — the analytics denominator for opens. `notifType`
 * is the raw `notif` value (null for an untyped legacy link).
 */
export interface NotificationLanding {
  channel: NotificationChannel;
  notifType: string | null;
  partnershipId: string | null;
}

const channelFromParams = (params: URLSearchParams): NotificationChannel =>
  params.get("src") === "email" ? "email" : "push";

/**
 * On web a notification tap arrives as URL params (`?notif=…&partnershipId=…`
 * from the push's `webpush.fcmOptions.link`, `&src=email` for email CTAs).
 * Queue-type pushes carry no data and land on `/app` plain — same as RN's
 * "opens app only".
 */
export const parseRemoteNotificationPressFromSearch = (
  search: string,
): RemoteNotificationPress | null => {
  const params = new URLSearchParams(search);
  const type = params.get("notif");
  const partnershipId = params.get("partnershipId");
  if (!partnershipId) {
    return null;
  }
  const channel = channelFromParams(params);
  const partnerId = params.get("partnerId") || undefined;
  if (type === PARTNER_DECK_ACTIVITY_NOTIFICATION_TYPE) {
    return { type, channel, press: { partnershipId, partnerId } };
  }
  if (type === PARTNER_ALL_CARDS_UNLOCKED_NOTIFICATION_TYPE) {
    return { type, channel, press: { partnershipId, partnerId } };
  }
  if (type === CHARACTER_MATCH_NOTIFICATION_TYPE) {
    return {
      type,
      channel,
      press: {
        partnershipId,
        characterId: params.get("characterId") || undefined,
      },
    };
  }
  return null;
};

/**
 * Non-null for every notification-link landing — whenever `notif` is present
 * (even empty) or the URL is an email CTA (`src=email`) — regardless of
 * whether the type has an in-app press destination.
 */
export const parseNotificationLandingFromSearch = (
  search: string,
): NotificationLanding | null => {
  const params = new URLSearchParams(search);
  const isEmail = params.get("src") === "email";
  if (params.get("notif") === null && !isEmail) {
    return null;
  }
  return {
    channel: isEmail ? "email" : "push",
    notifType: params.get("notif") || null,
    partnershipId: params.get("partnershipId") || null,
  };
};

/** True when the URL carries notification params worth stripping after capture. */
export const hasNotificationSearchParams = (search: string): boolean => {
  const params = new URLSearchParams(search);
  return (
    params.has("notif") || params.has("partnershipId") || params.has("src")
  );
};
